/*
 * Div2GIDFound: This component displays the GID information.
 *
 * It receives the entire userInfo returned by GET /gid?user=${email}.
 * This component/UI can decide what to display.
 */
import React from "react";
import "./Div2UserInfo.css";
import { useApiActivity } from "../../contexts/ApiActivityContext";

const Div2GIDFound = ({
  div2Title,
  userInfo,
  activeStep,
  handleUnlockAccountButtonPress,
}) => {
  const { isActive } = useApiActivity();
  if (!userInfo) {
    return null; // Don't render anything if userInfo isn't loaded yet
  }

  // If the user is deprovisioned, we need to disable the buttons
  let userDisabled = false;
  if (userInfo?.activeDirectory?.disabled === true) {
    userDisabled = true;
    userInfo.status = "DISABLED";
  } else {
    userDisabled = false;
    userInfo.status = "ACTIVE";
  }

  // Calculate the time when the user can change the password again
  var recentPasswordChange = false;
  const lastPasswordChangeTime = new Date(
    userInfo?.activeDirectory?.passwordLastSet
  );
  const validPasswordChangeTime = new Date(
    lastPasswordChangeTime.getTime() + 24 * 60 * 60 * 1000
  );
  const validPasswordChangeTimeStr = validPasswordChangeTime.toLocaleString();
  const now = Date.now();
  if (now < validPasswordChangeTime) {
    recentPasswordChange = true;
  }

  // Handle account expiration time
  const userAccountExpired = userInfo?.activeDirectory?.accountExpired;
  let accountExpirationTimeStr = "";
  // MS uses multiple values for "never" expire
  if (
    userInfo?.activeDirectory?.accountExpires === "30828-09-14T02:48:05.477Z" ||
    userInfo?.activeDirectory?.accountExpires ===
      "9999-12-31T23:59:59.999999+00:00" ||
    userInfo?.activeDirectory?.accountExpires === "1601-01-01T00:00:00+00:00" ||
    userInfo?.activeDirectory?.accountExpires === "1601-01-01T00:00:00.000Z" ||
    userInfo?.activeDirectory?.accountExpires === null
  ) {
    accountExpirationTimeStr = "Never";
  } else {
    const accountExpirationTime = new Date(
      userInfo?.activeDirectory?.accountExpires
    );
    accountExpirationTimeStr = accountExpirationTime.toLocaleString();
  }

  // Handle password expiration time
  const passwordExpired = userInfo?.activeDirectory?.passwordExpired;
  let passwordExpirationTimeStr = "";
  if (userInfo?.activeDirectory?.passwordNeverExpires === true) {
    passwordExpirationTimeStr = "Never";
  } else {
    const passwordExpirationTime = new Date(
      userInfo?.activeDirectory?.passwordExpires
    );
    passwordExpirationTimeStr = passwordExpirationTime.toLocaleString();
  }
  let passwordSetTimeStr = new Date(
    userInfo?.activeDirectory?.passwordLastSet
  ).toLocaleString();

  if (
    userInfo?.activeDirectory?.passwordLastSet ===
      "1601-01-01T00:00:00+00:00" ||
    userInfo?.activeDirectory?.passwordLastSet === null
  ) {
    passwordSetTimeStr = "NEVER";
  }
  if (
    userInfo?.activeDirectory?.passwordExpires ===
      "1601-01-01T00:00:00+00:00" ||
    userInfo?.activeDirectory?.passwordLastSet === null
  ) {
    passwordExpirationTimeStr = "EXPIRED";
  }

  // Handle last login time
  let lastLoginTimeStr = "";
  if (
    userInfo?.activeDirectory?.lastLogon === "1601-01-01T00:00:00+00:00" ||
    userInfo?.activeDirectory?.accountExpires === "1601-01-01T00:00:00.000Z" ||
    userInfo?.activeDirectory?.lastLogon === null
  ) {
    lastLoginTimeStr = "Never";
  } else {
    lastLoginTimeStr = new Date(
      userInfo?.activeDirectory?.lastLogon
    ).toLocaleString();
  }

  // Handle Locked Out status
  let accountLockedOut = false;
  if (userInfo?.activeDirectory?.locked === true) {
    accountLockedOut = true;
    userInfo.status = "LOCKED_OUT";
  }

  return (
    <div className={`child-div ${activeStep === 2 ? "active-step" : ""}`}>
      <div className="app-div-subtitle">
        <div className="title-container">
          {div2Title}
        </div>
      </div>
      <div className="user-info-container app-text">
        <div className="user-info">
          <strong>Name: </strong>
          {userInfo.firstName} {userInfo.lastName}
        </div>
        <div
          className={`user-info ${
            accountLockedOut || userDisabled ? "alert-text" : ""
          }`}
        >
          <strong>Account Status: </strong>
          {userInfo.status}
        </div>
        <div className="user-info">
          <strong>Last Login: </strong> {lastLoginTimeStr}
        </div>
        <div className="user-info">
          <strong>Last Password Change: </strong>
          {passwordSetTimeStr}
        </div>
        <div className={`user-info ${passwordExpired ? "alert-text" : ""}`}>
          <strong>
            {passwordExpired ? "Password Expired: " : "Password Expires: "}
          </strong>
          {passwordExpirationTimeStr}
        </div>
        <div className={`user-info ${userAccountExpired ? "alert-text" : ""}`}>
          <strong>
            {userAccountExpired ? "Account Expired: " : "Account Expires: "}
          </strong>
          {accountExpirationTimeStr}
        </div>
      </div>

      <div className="account-alert" hidden={!userDisabled}>
        <div className="alert-title">Account Disabled</div>
      </div>

      <div className="account-alert" hidden={!recentPasswordChange}>
        <div className="alert-title">Recent Password Change</div>
        <div className="alert-text">
          This account's password was recently changed. No password
          changes/resets will be possible until{" "}
          <span className="bold">{validPasswordChangeTimeStr}</span> due to the
          Minimum Password Age policy, which is currently set to 1 day
        </div>
      </div>

      <div
        className="account-alert"
        hidden={!passwordExpired || userDisabled || userAccountExpired}
      >
        <div className="alert-title">Password Change Required</div>
        <div className="alert-text">
          This user must change their password the next time they login.
        </div>
      </div>

      <button
        hidden={!accountLockedOut}
        className={`app-button button-under ${
          isActive ? "button-invalid" : "button-valid"
        }`}
        disabled={isActive}
        onClick={handleUnlockAccountButtonPress}
      >
        Unlock Account
      </button>
    </div>
  );
};

export default Div2GIDFound;
